import {
  CloudUpload as CloudUploadIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  IFTA_SURCHARGE_CODES,
  uploadIftaTaxRates,
  useGetIftaSurchargesQuery,
  useSaveIftaSurchargesMutation,
} from "../../api/reportsApi";
import QuarterPicker, { currentQuarter } from "./QuarterPicker";

interface IftaTaxAdminPanelProps {
  token: string;
}

const sectionTitleSx = {
  color: "text.primary",
  fontWeight: 600,
} as const;

const IftaTaxAdminPanel: React.FC<IftaTaxAdminPanelProps> = ({ token }) => {
  const [taxQuarter, setTaxQuarter] = useState(currentQuarter());
  const [taxFile, setTaxFile] = useState<File | null>(null);
  const [taxUploadLoading, setTaxUploadLoading] = useState(false);
  const [taxUploadError, setTaxUploadError] = useState<string | null>(null);
  const [taxUploadSuccess, setTaxUploadSuccess] = useState<string | null>(null);

  const { data: surchargeData, isLoading: surchargesLoading } =
    useGetIftaSurchargesQuery();
  const [saveSurcharges, { isLoading: savingSurcharges }] =
    useSaveIftaSurchargesMutation();

  const [surchargeValues, setSurchargeValues] = useState<Record<string, string>>({});
  const [surchargeError, setSurchargeError] = useState<string | null>(null);
  const [surchargeSuccess, setSurchargeSuccess] = useState<string | null>(null);

  useEffect(() => {
    const initial: Record<string, string> = {};
    IFTA_SURCHARGE_CODES.forEach(({ code }) => {
      const existing = surchargeData?.find(
        (r) => r.jurisdictionCode.toUpperCase() === code.toUpperCase()
      );
      initial[code] = existing != null ? String(existing.rate) : "";
    });
    setSurchargeValues(initial);
  }, [surchargeData]);

  const onTaxDrop = useCallback((accepted: File[]) => {
    if (accepted.length > 0) {
      setTaxFile(accepted[0]);
      setTaxUploadError(null);
      setTaxUploadSuccess(null);
    }
  }, []);

  const { getRootProps: getTaxRootProps, getInputProps: getTaxInputProps, isDragActive: taxDragActive } =
    useDropzone({
      onDrop: onTaxDrop,
      accept: {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
        "application/vnd.ms-excel": [".xls"],
        "text/csv": [".csv"],
      },
      maxFiles: 1,
      disabled: taxUploadLoading,
    });

  const handleTaxUpload = async () => {
    if (!taxFile) {
      setTaxUploadError("Please select a tax rates file to upload.");
      return;
    }
    setTaxUploadLoading(true);
    setTaxUploadError(null);
    setTaxUploadSuccess(null);
    try {
      await uploadIftaTaxRates(taxFile, taxQuarter, token);
      setTaxUploadSuccess(`Tax rates for ${taxQuarter} uploaded successfully.`);
      setTaxFile(null);
    } catch (err) {
      setTaxUploadError(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setTaxUploadLoading(false);
    }
  };

  const handleSaveSurcharges = async () => {
    setSurchargeError(null);
    setSurchargeSuccess(null);

    const rates: { jurisdictionCode: string; rate: number }[] = [];

    for (const { code } of IFTA_SURCHARGE_CODES) {
      const raw = surchargeValues[code]?.trim();
      if (!raw) continue;

      const rate = Number(raw);
      if (Number.isNaN(rate)) {
        setSurchargeError(`"${code}" must be a numeric value or left empty to use the template default.`);
        return;
      }

      rates.push({ jurisdictionCode: code, rate });
    }

    try {
      await saveSurcharges(rates).unwrap();
      setSurchargeSuccess(
        rates.length === 0
          ? "Surcharge overrides cleared. Reports will use template defaults."
          : `Saved ${rates.length} surcharge override(s). Empty fields use template defaults.`
      );
    } catch (err) {
      setSurchargeError(
        err && typeof err === "object" && "data" in err
          ? String((err as { data?: { message?: string } }).data?.message ?? "Save failed.")
          : "Save failed."
      );
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload quarterly tax rates from iftach.org and manage global surcharge values used in reports.
      </Typography>

      <Typography variant="subtitle1" sx={{ ...sectionTitleSx, mb: 1 }}>
        Quarterly Tax Rates Upload
      </Typography>
      <Box sx={{ mb: 2, maxWidth: 280 }}>
        <QuarterPicker value={taxQuarter} onChange={setTaxQuarter} />
      </Box>

      <Box
        {...getTaxRootProps()}
        sx={{
          border: "2px dashed",
          borderColor: taxDragActive ? "primary.main" : "divider",
          borderRadius: 1,
          p: 3,
          textAlign: "center",
          cursor: taxUploadLoading ? "not-allowed" : "pointer",
          mb: 2,
          bgcolor: "background.paper",
        }}
      >
        <input {...getTaxInputProps()} />
        <CloudUploadIcon color="action" sx={{ fontSize: 40, mb: 1 }} />
        <Typography variant="body2" color="text.primary">
          {taxFile
            ? taxFile.name
            : taxDragActive
              ? "Drop the file here…"
              : "Drag & drop tax rates file (XLS, XLSX, CSV)"}
        </Typography>
      </Box>

      <Button
        variant="contained"
        startIcon={taxUploadLoading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
        onClick={handleTaxUpload}
        disabled={taxUploadLoading || !taxFile}
      >
        Upload Tax Rates for {taxQuarter}
      </Button>

      {taxUploadError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {taxUploadError}
        </Alert>
      )}
      {taxUploadSuccess && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {taxUploadSuccess}
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" sx={{ ...sectionTitleSx, mb: 1 }}>
        Global Surcharge Rates
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Fill only the surcharges you want to override. Empty fields keep the Excel template default in reports.
        Clear a saved value and save to revert that surcharge to the template.
      </Typography>

      {surchargesLoading ? (
        <CircularProgress size={24} />
      ) : (
        <Grid container spacing={2}>
          {IFTA_SURCHARGE_CODES.map(({ code, label }) => (
            <Grid item xs={12} sm={6} md={4} key={code}>
              <TextField
                fullWidth
                size="small"
                label={label}
                type="number"
                inputProps={{ step: "0.0001", min: 0 }}
                placeholder="Template default"
                value={surchargeValues[code] ?? ""}
                onChange={(e) =>
                  setSurchargeValues((prev) => ({ ...prev, [code]: e.target.value }))
                }
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Button
        variant="contained"
        sx={{ mt: 2 }}
        startIcon={savingSurcharges ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
        onClick={handleSaveSurcharges}
        disabled={savingSurcharges || surchargesLoading}
      >
        Save Surcharge Rates
      </Button>

      {surchargeError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {surchargeError}
        </Alert>
      )}
      {surchargeSuccess && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {surchargeSuccess}
        </Alert>
      )}
    </Box>
  );
};

export default IftaTaxAdminPanel;
